import classNames from 'classnames';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import {
    MaterialIdsType,
    type IListStyleMaterial,
    type ITextLayerProps,
} from '@meitu/saas-editor-core/es/version-types';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { ListStyleStruct } from '@/types';
import { ListStyleSelector } from '@/widgets/list-style-selector';
import { useEffectiveShapeExtension, useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';
import '@/styles/shared.less';

type Props = WidgetCommonProps & {
    listStyles: ListStyleStruct[];
};

export const ListStyle: React.FC<Props> = ({ layerId, recordId, isIntl, listStyles }) => {
    const editor = useEditor();

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const materialIds = layer.materialIds;
        const textListMaterials = materialIds?.[MaterialIdsType.TEXT_LIST] || [];
        const textListMaterialId = textListMaterials.length > 0 ? textListMaterials[0] : '';

        const layerData = layer.data as ITextLayerProps;
        const { listStyleMaterial } = layerData;

        return {
            textListMaterialId,
            listStyleMaterial,
        };
    });

    const shapeExtension = useEffectiveShapeExtension(layerId);
    const textListMaterialId = props ? props.textListMaterialId : '';
    const listStyleMaterial = props ? props.listStyleMaterial : undefined;

    const disableListStyle = !!shapeExtension;

    const handleSetListStyle = (data: ListStyleStruct) => {
        if (data.id === textListMaterialId) return;

        editor.getInstance<TextLayer>('TextLayer').applyListText(layerId, data);

        Tracker.setListText({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            listStyle: data.style,
        });
    };

    const handleCancelListStyle = () => {
        editor.getInstance<TextLayer>('TextLayer').cancelListText(layerId);
    };

    const handleListStyleOpenChange = (visible: boolean) => {
        popoverWidgetRef.current = visible;
    };

    const handleUpdateListStyleConfig = (config: IListStyleMaterial, options: { writeHistory: boolean }) => {
        editor.getInstance<TextLayer>('TextLayer').updateListStyleMaterial(layerId, config, options);
    };

    return (
        <div className={classNames('list-style', { intl: isIntl })}>
            <ListStyleSelector
                disabled={disableListStyle}
                list={listStyles}
                activeId={textListMaterialId}
                activeConfig={listStyleMaterial}
                onCancelListStyle={handleCancelListStyle}
                onSetListStyle={handleSetListStyle}
                onUpdateConfig={handleUpdateListStyleConfig}
                onOpenChange={handleListStyleOpenChange}
            />
        </div>
    );
};
